/**
 * AgentWidget — floating assistant launcher + chat panel.
 *
 * Mounted once in Layout.js so it floats over every authenticated page.
 *
 * Design notes:
 *  - Admin only. POST /api/agent/chat is gated by get_admin_user, so showing
 *    the button to a broker/syndicator would only produce 403s.
 *  - Agent replies are PRE-FORMATTED text (the payoff / payment breakdowns are
 *    rendered server-side in Python so the numbers are exact). They are shown
 *    in a monospace block — do NOT reflow or markdown-render them.
 *  - When the backend returns `staged`, a write is pending. We surface explicit
 *    Confirm / Cancel buttons instead of relying on the operator typing the
 *    word, and we visually lock the panel into "awaiting confirmation" state.
 *  - Dates arrive already formatted MM-DD-YYYY. No client-side date handling.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bot, Send, Loader2, AlertTriangle, CheckCircle2,
  Minimize2, Trash2, Circle, Paperclip,
  DollarSign, FilePlus2, FileText, Search, RefreshCw, UserPlus,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tokenStorage } from '../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback only — the real name and shortcuts come from
// Settings > AI Assistant (GET /api/agent/config).
const FALLBACK_NAME = 'Assistant';
const FALLBACK_SHORTCUTS = [
  'refied 10458 to 10899',
  '10879 sent zelle for 300',
  "what's the balance on 10458?",
];

const AgentWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [staged, setStaged] = useState(null);
  const [dealPrefill, setDealPrefill] = useState(null); // new-deal draft → wizard
  const [dealOffer, setDealOffer] = useState(null); // contact created → offer same-doc deal
  const [documentDraft, setDocumentDraft] = useState(null); // MILA payoff draft → preview
  const [health, setHealth] = useState(null); // null = unknown
  const [modelInfo, setModelInfo] = useState(null); // { label, local }
  const [config, setConfig] = useState(null); // { assistant_name, shortcuts }
  const [hydrated, setHydrated] = useState(false); // loaded from localStorage yet?

  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  // P4.3.2: which workflow the next chosen PDF feeds — 'deal' (default,
  // New-Deal prefill) or 'contact' (Create Contact from Document).
  const docModeRef = useRef('deal');
  const [dupFile, setDupFile] = useState(null);

  const isAdmin = user?.roles?.includes('admin');

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${tokenStorage.getToken()}` } }),
    []
  );

  // Persist the conversation in the browser so it survives page switches and
  // full reloads. The server already keeps the real session (agent_sessions in
  // Mongo) keyed by session_id, so re-sending the same session_id also restores
  // any staged (pending) write — CONFIRM keeps working after a reload.
  // Keyed per user so switching operator accounts never leaks a conversation.
  const storageKey = user?.id ? `pfs_agent_state_v1:${user.id}` : null;

  // Hydrate once, as soon as we know which user we are.
  useEffect(() => {
    if (!storageKey || hydrated) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved.messages)) setMessages(saved.messages);
        if (saved.sessionId) setSessionId(saved.sessionId);
        if (saved.staged) setStaged(saved.staged);
        if (saved.dealOffer) setDealOffer(saved.dealOffer);
        if (saved.open) setOpen(true);
      }
    } catch {
      /* corrupt or unavailable storage — start fresh, non-fatal */
    }
    // Flip the flag in the same batch as the setState calls above, so the
    // persist effect below never fires against the empty initial state first.
    setHydrated(true);
  }, [storageKey, hydrated]);

  // Persist on every change — but only after hydration, and cap history so a
  // long-running conversation can't blow the localStorage quota.
  useEffect(() => {
    if (!storageKey || !hydrated) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          messages: messages.slice(-100),
          sessionId,
          staged,
          dealOffer,
          open,
        })
      );
    } catch {
      /* quota exceeded / private mode — non-fatal */
    }
  }, [storageKey, hydrated, messages, sessionId, staged, dealOffer, open]);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Re-check the local model EVERY time the panel opens.
  // (Do not add `health` to the deps or guard on it — a one-shot check gets
  // stuck showing "offline" forever after a transient failure or a backend
  // restart, which is exactly what happened during setup.)
  const checkHealth = useCallback(() => {
    setHealth('checking');
    return axios
      .get(`${API}/agent/health`, authHeaders())
      .then((r) => {
        setHealth(r.data?.ok ? 'up' : 'down');
        setModelInfo({
          label: r.data?.model_label || r.data?.model || 'model',
          local: !!r.data?.local,
        });
      })
      .catch(() => setHealth('down'));
  }, [authHeaders]);

  useEffect(() => {
    if (!open || !isAdmin) return;
    checkHealth();
    axios
      .get(`${API}/agent/config`, authHeaders())
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, [open, isAdmin, checkHealth, authHeaders]);

  // Cmd/Ctrl + J toggles the panel
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const push = (role, text, extra = {}) =>
    setMessages((m) => [...m, { role, text, ts: Date.now(), ...extra }]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;

    // P4.3.2 natural-language routing: "create a contact from this contract"
    // reaches the SAME staged contact_from_document workflow (not the deal
    // path, not a direct write). Skip when a confirmation is already pending.
    if (!staged && detectContactIntent(text)) {
      push('user', text);
      setInput('');
      docModeRef.current = 'contact';
      push('agent', 'Sure — attach the contract or document (PDF) and I\u2019ll pull the contact from it.');
      fileInputRef.current?.click();
      return;
    }

    push('user', text);
    setInput('');
    setBusy(true);
    setDealPrefill(null); // a new turn supersedes any pending draft button
    setDocumentDraft(null);

    try {
      // Structured CRM context: the deal the operator currently has open.
      // Takes priority over any deal number MILA might infer from the message.
      const activeDeal =
        (typeof window !== 'undefined' && window.__PFS_ACTIVE_DEAL_NUMBER__) || null;
      const res = await axios.post(
        `${API}/agent/chat`,
        { message: text, session_id: sessionId, deal_id: activeDeal },
        authHeaders()
      );
      const data = res.data || {};
      if (data.session_id) setSessionId(data.session_id);
      setStaged(data.staged || null);
      setDealPrefill(data.deal_prefill || null);
      setDealOffer(data.deal_offer || null);
      setDocumentDraft(data.document_draft || null);
      push('agent', data.reply || '(no response)', {
        posted: !!data.posted,
        pending: !!data.staged,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 503) {
        msg = detail
          ? `Model unreachable.\n\n${detail}`
          : 'The model is not reachable. If AGENT_MODEL is a local ollama model, '
            + 'start it with:\n\n  ollama run qwen2.5:14b\n\n'
            + 'If it is a cloud model, check the API key on the backend.';
        setHealth('down');
      } else if (status === 403) {
        msg = 'Admin access is required to use the assistant.';
      } else {
        msg = `Error: ${detail || err.message}`;
      }
      push('agent', msg, { error: true });
    } finally {
      setBusy(false);
    }
  };

  // Dispatch by file type: CSV → payments preflight; PDF → contract extraction.
  const uploadFile = (file) => {
    if (!file || uploading || busy) return undefined;
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.pdf')) {
      const mode = docModeRef.current;
      docModeRef.current = 'deal';            // reset after consuming
      return mode === 'contact'
        ? uploadContactFromDocument(file)
        : uploadContractPdf(file);
    }
    if (lower.endsWith('.csv')) return uploadPaymentsCsv(file);
    push('agent',
      `"${file.name}" isn’t a CSV or PDF. Upload a payments/bounces CSV or a contract PDF.`,
      { error: true });
    return undefined;
  };

  const uploadPaymentsCsv = async (file) => {
    push('user', `📎 ${file.name}`);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (sessionId) form.append('session_id', sessionId);
      form.append('skip_dedup', 'false');

      const res = await axios.post(`${API}/agent/upload`, form, {
        headers: {
          ...authHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = res.data || {};
      if (data.session_id) setSessionId(data.session_id);
      setStaged(data.staged || null);
      push('agent', data.reply || '(no response)', {
        pending: !!data.staged,
        error: !data.staged,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      push('agent', `Upload failed: ${detail || err.message}`, { error: true });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Contract PDF → extract terms → pre-fill the New Deal wizard (writes nothing).
  const uploadContractPdf = async (file) => {
    push('user', `📎 ${file.name}`);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (sessionId) form.append('session_id', sessionId);

      const res = await axios.post(`${API}/agent/extract-contract`, form, {
        headers: {
          ...authHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = res.data || {};
      if (data.status === 'ready' && data.prefill) {
        push('agent', data.reply || 'Extracted the contract — opening the New Deal form…');
        // Hand the extracted values to DealWizard, which reads this on mount.
        sessionStorage.setItem('prefillDealWizard', JSON.stringify(data.prefill));
        setOpen(false);
        navigate('/deals?createNew=true');
      } else {
        push('agent', data.reply || 'I couldn’t pull deal terms from that contract.',
          { error: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      push('agent', `Contract read failed: ${detail || err.message}`, { error: true });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Mirrors backend document_intake.detect_contact_from_document_intent so a
  // typed request routes to the same workflow the button uses.
  const detectContactIntent = (t) => {
    const x = (t || '').toLowerCase();
    if (!x) return false;
    const contact = /(contact|merchant|customer|client|business|company)/;
    const verbFromDoc = /(create|add|make|set ?up|pull|extract|import|onboard)[^.]*(contact|merchant|customer|client|business|company)[^.]*(from|out of|using|off)[^.]*(document|contract|pdf|attachment|file|agreement)/;
    const thisContact = /(create|add|make|onboard)[^.]*this[^.]*(contact|merchant|customer|client|business|company)/;
    const pullInfo = /pull[^.]*contact (info|information|details)/;
    const nounFromDoc = /(contact|merchant|customer|client|business|company)[^.]*from (this|the|attached)[^.]*(document|contract|pdf|attachment|file|agreement)/;
    const isContact = verbFromDoc.test(x) || thisContact.test(x) || pullInfo.test(x) || nounFromDoc.test(x);
    if (!isContact) return false;
    const isDeal = /(deal|advance|funding|refi|refinance)/.test(x);
    if (isDeal && !contact.test(x)) return false;
    return true;
  };

  // P4.3.2 — Create/Update a Contact from a document via the SAME backend
  // workflow (POST /agent/contact-from-document). Writes nothing; the staged
  // preview -> CONFIRM gate (server-side session) creates/updates the contact.
  const uploadContactFromDocument = async (file, mode = 'auto') => {
    push('user', `\uD83D\uDCCE ${file.name}`);
    setUploading(true);
    setDupFile(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (sessionId) form.append('session_id', sessionId);
      form.append('mode', mode);
      const res = await axios.post(`${API}/agent/contact-from-document`, form, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};
      if (data.session_id) setSessionId(data.session_id);
      setStaged(data.staged || null);
      push('agent', data.reply || '(no response)', {
        pending: !!data.staged, error: !data.staged && mode !== 'auto',
      });
      // Strong duplicate: no staged create; offer update from the same file.
      if (!data.staged && mode === 'auto') setDupFile(file);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      push('agent', `Document read failed: ${detail || err.message}`, { error: true });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const clearSession = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/agent/session/${sessionId}`, authHeaders());
      } catch {
        /* non-fatal — the server drops stale sessions anyway */
      }
    }
    setSessionId(null);
    setMessages([]);
    setStaged(null);
    setDealPrefill(null);
    setDealOffer(null);
    setDocumentDraft(null);
  };

  // Intent picker (shown when the panel opens): pre-ask what the operator wants.
  // Text intents seed the composer so they just edit the specifics; action
  // intents (new deal / from a contract) fire directly.
  const handleIntent = (key) => {
    if (key === 'newdeal') { setOpen(false); navigate('/deals?createNew=true'); return; }
    if (key === 'contract') { fileInputRef.current?.click(); return; }
    if (key === 'contact_from_document') {
      docModeRef.current = 'contact';
      fileInputRef.current?.click();
      return;
    }
    const seed = {
      payment: '10001 sent zelle for 500',
      lookup: "what's the balance on 10001?",
      refi: 'refied 10001 to 10002',
    }[key];
    if (seed) { setInput(seed); setTimeout(() => inputRef.current?.focus(), 0); }
  };

  if (!isAdmin) return null;

  // ── Launcher ────────────────────────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Assistant  (Ctrl/Cmd + J)"
        aria-label="Open assistant"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-blue-600 text-white
                   shadow-lg shadow-blue-600/30 flex items-center justify-center
                   hover:bg-blue-700 hover:scale-105 active:scale-95
                   transition-all focus:outline-none focus:ring-2 focus:ring-blue-500
                   focus:ring-offset-2"
      >
        <Bot className="h-6 w-6" />
        {staged && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-400
                           border-2 border-white animate-pulse" />
        )}
      </button>
    );
  }

  // ── Panel ───────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col w-[95vw] sm:w-[440px]
                 h-[600px] max-h-[85vh] bg-white rounded-xl border border-slate-200
                 shadow-2xl overflow-hidden"
      role="dialog"
      aria-label="PFS assistant"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      {dragOver && (
        <div className="absolute inset-0 z-10 bg-blue-50/95 border-2 border-dashed
                        border-blue-400 rounded-xl flex flex-col items-center
                        justify-center pointer-events-none">
          <Paperclip className="h-8 w-8 text-blue-500 mb-2" />
          <p className="text-sm font-medium text-blue-900">Drop a payments CSV or a contract PDF</p>
          <p className="text-xs text-blue-600 mt-1">
            CSV runs a preflight; a PDF pre-fills a New Deal — nothing saves until you confirm
          </p>
        </div>
      )}
      {/* Header */}
      <div className="h-14 px-4 flex items-center justify-between border-b bg-slate-50 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-blue-600 text-white flex items-center
                          justify-center shrink-0">
            <Bot className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 leading-tight">
              {config?.assistant_name || FALLBACK_NAME}
            </p>
            <button
              type="button"
              onClick={checkHealth}
              title="Click to re-check the local model"
              className="text-xs text-slate-500 flex items-center gap-1 leading-tight
                         hover:text-slate-700"
            >
              <Circle
                className={`h-2 w-2 fill-current ${
                  health === 'up'
                    ? 'text-emerald-500'
                    : health === 'down'
                    ? 'text-red-500'
                    : 'text-slate-300'
                }`}
              />
              {health === 'up'
                ? `${modelInfo?.label || 'model'} · ${modelInfo?.local ? 'local' : 'cloud'}`
                : health === 'down' ? 'model offline — click to retry'
                : 'checking…'}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={clearSession}
            title="Clear conversation"
            className="p-2 rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Minimize"
            className="p-2 rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-700"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white">
        {messages.length === 0 && (
          <div className="text-center pt-6">
            <div className="h-12 w-12 rounded-xl bg-slate-100 text-slate-400 flex items-center
                            justify-center mx-auto mb-3">
              <Bot className="h-6 w-6" />
            </div>
            <p className="text-sm text-slate-600 mb-1">Ask in plain language.</p>
            <p className="text-xs text-slate-400 mb-4">
              Nothing posts until you confirm it.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mb-4">
              {[
                { key: 'payment', label: 'Record a payment', Icon: DollarSign },
                { key: 'newdeal', label: 'New deal', Icon: FilePlus2 },
                { key: 'contract', label: 'From a contract', Icon: FileText },
                { key: 'contact_from_document', label: 'Contact from doc', Icon: UserPlus },
                { key: 'lookup', label: 'Look up', Icon: Search },
                { key: 'refi', label: 'Refinance', Icon: RefreshCw },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleIntent(key)}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full
                             border border-slate-200 text-slate-600 hover:border-blue-400
                             hover:bg-blue-50 hover:text-blue-700 transition-colors"
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mb-2">or a saved shortcut:</p>
            <div className="space-y-2">
              {(config?.shortcuts ?? FALLBACK_SHORTCUTS).map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => send(ex)}
                  className="block w-full text-left text-xs px-3 py-2 rounded-lg border
                             border-slate-200 text-slate-600 hover:border-blue-400
                             hover:bg-blue-50 hover:text-blue-700 transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-lg rounded-br-sm bg-blue-600 text-white
                              px-3 py-2 text-sm break-words">
                {m.text}
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div
                className={`max-w-[95%] rounded-lg rounded-bl-sm px-3 py-2 text-xs border
                  ${m.error
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : m.posted
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : m.pending
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-slate-50 border-slate-200 text-slate-800'}`}
              >
                {m.posted && (
                  <div className="flex items-center gap-1 font-semibold mb-1">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Posted
                  </div>
                )}
                {m.pending && (
                  <div className="flex items-center gap-1 font-semibold mb-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Awaiting your confirmation
                  </div>
                )}
                <pre className="whitespace-pre-wrap font-mono leading-relaxed text-[11px]">
                  {m.text}
                </pre>
              </div>
            </div>
          )
        )}

        {(busy || uploading) && (
          <div className="flex items-center gap-2 text-xs text-slate-400 px-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {uploading ? 'checking the file…' : 'thinking…'}
          </div>
        )}
      </div>

      {/* P4.3.2 — strong duplicate: offer Update from Document (no auto-create) */}
      {dupFile && !staged && !busy && (
        <div className="px-4 py-3 border-t shrink-0 bg-sky-50">
          <p className="text-xs text-sky-900 mb-2 font-medium">
            That merchant already exists. Update it from this document instead?
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => uploadContactFromDocument(dupFile, 'update')}
              className="flex-1 h-9 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium">
              Update from document
            </button>
            <button type="button" onClick={() => setDupFile(null)}
              className="flex-1 h-9 rounded-md bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Confirmation bar — only when a write is staged */}
      {staged && !busy && (
        <div className={`px-4 py-3 border-t shrink-0 ${
          staged.requires_signoff ? 'bg-red-50' : 'bg-amber-50'
        }`}>
          {staged.requires_signoff ? (
            <>
              <p className="text-xs text-red-900 mb-1 font-semibold flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Operator sign-off required
              </p>
              <ul className="text-xs text-red-800 mb-2 list-disc list-inside">
                {(staged.signoff_reasons || []).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </>
          ) : (
            <p className="text-xs text-amber-900 mb-2 font-medium">
              Review the breakdown above. This will write to the ledger.
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => send('CONFIRM')}
              className={`flex-1 h-9 rounded-md text-white text-sm font-medium
                          active:scale-[0.99] transition ${
                staged.requires_signoff
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {staged.requires_signoff ? 'Sign off & post' : 'Confirm & post'}
            </button>
            <button
              type="button"
              onClick={() => send('cancel')}
              className="flex-1 h-9 rounded-md bg-white border border-slate-300 text-slate-700
                         text-sm font-medium hover:bg-slate-50 active:scale-[0.99] transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* New-deal draft → open the pre-filled wizard to finish + save */}
      {dealPrefill && !busy && (
        <div className="px-4 py-3 border-t bg-blue-50 shrink-0">
          <p className="text-xs text-blue-900 mb-2 font-medium">
            Draft ready. Open the New Deal form to add the funder / ISO / indicators and save —
            nothing is created until you save there.
          </p>
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.setItem('prefillDealWizard', JSON.stringify(dealPrefill));
              } catch { /* private mode — non-fatal */ }
              setDealPrefill(null);
              setOpen(false);
              navigate('/deals?createNew=true');
            }}
            className="w-full h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm
                       font-medium active:scale-[0.99] transition"
          >
            Open the deal to finish it →
          </button>
        </div>
      )}

      {/* Contact created from document → reuse its extracted terms for a deal */}
      {dealOffer && !staged && !dealPrefill && !busy && (
        <div className="px-4 py-3 border-t bg-emerald-50 shrink-0">
          <p className="text-xs text-emerald-900 mb-2 font-medium">
            Create a new deal for {dealOffer.merchant_name || 'this contact'} using the same document?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => send('YES')}
              className="flex-1 h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm
                         font-medium active:scale-[0.99] transition"
            >
              Yes, prepare deal
            </button>
            <button
              type="button"
              onClick={() => send('no')}
              className="flex-1 h-9 rounded-md bg-white border border-slate-300 text-slate-700
                         text-sm font-medium hover:bg-slate-50 active:scale-[0.99] transition"
            >
              No, contact only
            </button>
          </div>
        </div>
      )}

      {/* MILA payoff DRAFT → preview (not approved / not sent) */}
      {documentDraft && !busy && (
        <div className="px-4 py-3 border-t bg-amber-50 shrink-0">
          <p className="text-xs text-amber-900 mb-2 font-medium">
            Payoff DRAFT ready (v{documentDraft.version_number}) — not approved, not sent.
          </p>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await axios.get(
                  `${process.env.REACT_APP_BACKEND_URL}${documentDraft.preview_path}`,
                  { ...authHeaders(), responseType: 'blob' }
                );
                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                window.open(url, '_blank', 'noopener');
              } catch {
                push('agent', 'Could not open the draft preview.', {});
              }
            }}
            className="w-full h-9 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm
                       font-medium active:scale-[0.99] transition"
          >
            Preview Payoff Letter (DRAFT) →
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="p-3 border-t bg-white shrink-0">
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv,.pdf,application/pdf"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy || uploading}
            title="Attach a payments CSV or a contract PDF"
            className="h-9 w-9 shrink-0 rounded-lg border border-slate-300 text-slate-500
                       flex items-center justify-center hover:bg-slate-50
                       hover:text-slate-700 disabled:opacity-40
                       disabled:cursor-not-allowed transition"
          >
            {uploading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Paperclip className="h-4 w-4" />}
          </button>
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={staged ? 'Or tell me what to change…' : 'Ask or instruct…'}
            disabled={busy}
            className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm
                       max-h-32 focus:outline-none focus:ring-2 focus:ring-blue-500
                       focus:border-transparent disabled:bg-slate-50"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="h-9 w-9 shrink-0 rounded-lg bg-blue-600 text-white flex items-center
                       justify-center hover:bg-blue-700 disabled:opacity-40
                       disabled:cursor-not-allowed transition"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 px-1">
          {modelInfo && !modelInfo.local
            ? 'Deal data is sent to the model provider · writes require confirmation'
            : 'Runs locally · nothing leaves this machine · writes require confirmation'}
        </p>
      </div>
    </div>
  );
};

export default AgentWidget;
